// Client-side mirror of services/bookings/lib/cancellationPolicy.js's
// derive() — used only to decide what the Cancel modal should show
// (which vendor, whether there's a PNR to type-confirm). The backend
// remains the source of truth; this never decides whether cancel is
// actually allowed.
const PARTNER_TO_PROVIDER = { A: "AMADEUS", TR: "TRIPS", T: "TIQWA" };
const FLIGHT_TYPES = new Set(["FLIGHT", "FLIGHT-RESERVATION"]);

export const deriveBookingMeta = (booking) => {
  const flightOffer = booking?.booking_reference?.data?.flightOffers?.[0];
  const partner = flightOffer?.tramangoPartner ?? null;
  const provider = PARTNER_TO_PROVIDER[partner] ?? null;

  const associatedRecord =
    booking?.booking_reference?.data?.associatedRecords?.[0];
  const pnr = associatedRecord?.reference ?? null;

  const rawId = booking?.booking_reference?.data?.id;
  let amadeusOrderId = null;
  if (rawId) {
    try {
      amadeusOrderId = decodeURIComponent(rawId);
    } catch {
      amadeusOrderId = rawId;
    }
  }

  const providerReference = provider === "AMADEUS" ? amadeusOrderId : pnr;
  const providerOffice = flightOffer?.tramangoOffice ?? null;
  const ticketed =
    Boolean(associatedRecord?.tickets?.length) || booking?.purchased === true;
  const isFlight = FLIGHT_TYPES.has(booking?.type);
  const isPaid = booking?.payment_status === "PAID";

  return {
    partner,
    provider,
    pnr,
    amadeusOrderId,
    providerReference,
    hasProviderRef: Boolean(providerReference),
    providerOffice,
    ticketed,
    isFlight,
    isPaid,
  };
};

export const describeCancelEffect = (booking) => {
  const meta = deriveBookingMeta(booking);

  if (!meta.isFlight) {
    if (booking.status === "CONFIRMED") {
      return "No vendor integration for this booking type — a SUPER admin must force-cancel.";
    }
    return "No vendor call — this will only be marked CANCELLED internally.";
  }

  if (meta.hasProviderRef) {
    return `${meta.provider} ${meta.provider === "AMADEUS" ? "order" : "PNR"} ${
      meta.providerReference
    } will be cancelled at the vendor, then the booking marked CANCELLED.`;
  }

  if (booking.status === "CONFIRMED") {
    return "No vendor reference exists for this booking — a SUPER admin must force-cancel.";
  }

  return "No vendor call — there's no PNR yet; this will only be marked CANCELLED internally.";
};

export const getProviderLabel = (booking) => {
  const offer = booking?.booking_reference?.data?.flightOffers?.[0];
  switch (offer?.tramangoPartner) {
    case "TR":
      return "Trips";
    case "A":
      if (offer.tramangoOffice === "GBR") return "Amadeus UK";
      if (offer.tramangoOffice === "LOS") return "Amadeus LOS";
      return "Amadeus";
    case "T":
      return "Tiqwa";
    default:
      return null;
  }
};

export const getPaymentLabel = (booking) => {
  const method = booking?.paymentMethod;
  if (booking?.payment_status !== "PAID" || !method || method === "PENDING") {
    return "Not paid";
  }
  const name = method.charAt(0) + method.slice(1).toLowerCase();
  return `Paid via ${name}`;
};
